import './style/Shipments.css';
import React, { useEffect, useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { openTrayTypeWithData } from '../../redux/keystoneSlice';
import Placeholder from './Placeholder';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { normalizeTabName, fetchShipments } from '../../redux/keystoneInterfaceSlice';
import { Text } from '@harperai/materialize-design-system';
import AwaitingShipment from '../../media/awaiting_shipment.png';
import ShipmentShipped from '../../media/shipment_shipped.png';
import ShipmentDelivered from '../../media/shipment_delivered.png';
import { colorFromNumber} from '../../util/Calculate';

const Shipments = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const keystoneInterfaceShipmentsTabs = useSelector((state) => state?.keystoneInterface?.shipmentsTabs);
    const keystoneInterfaceAccessToken = useSelector((state) => state?.keystoneInterface?.accessToken);
    const keystoneInventoryShipments = useSelector((state) => state?.keystoneInventory?.shipments);
    const keystoneInterfaceFetchedShipments = useSelector((state) => state?.keystoneInterface?.fetchedShipments);

    const { search } = useLocation();
    const selectedObjectId = new URLSearchParams(search).get('id') || null;
    const selectedObject = useMemo(() => keystoneInventoryShipments?.find(offer => offer.id === selectedObjectId), [keystoneInventoryShipments, selectedObjectId]);
    const { page, tabName } = useParams();

    const keystoneInterfaceShipmentsTab = keystoneInterfaceShipmentsTabs.map(normalizeTabName).indexOf(normalizeTabName(tabName));

    useEffect(() => {
        const matchingShipment = keystoneInventoryShipments?.find(shipment => shipment.id === selectedObjectId);
        if (!matchingShipment && selectedObjectId && keystoneInterfaceFetchedShipments) {
            navigate(`/${page}/${tabName}`);
        } else if (matchingShipment?.status === "PENDING" && tabName !== "awaiting-shipment") {
            navigate(`/${page}/awaiting-shipment?id=${matchingShipment.id}`);
        } else if ((matchingShipment?.status === "SHIPPED" || matchingShipment?.status === "COMPLETED") && tabName !== "shipped") {
            navigate(`/${page}/shipped?id=${matchingShipment.id}`);
        }
    }, [keystoneInterfaceFetchedShipments, keystoneInventoryShipments, navigate, page, selectedObjectId, tabName]);

    useEffect(() => {
        if (keystoneInterfaceShipmentsTab < 0 && !selectedObjectId) {
            navigate(`/${page}/${normalizeTabName(keystoneInterfaceShipmentsTabs?.[0])}`);
        }
    }, [keystoneInterfaceShipmentsTab, keystoneInterfaceShipmentsTabs, page, navigate, selectedObjectId]);

    useEffect(() => {
        dispatch(fetchShipments({ accessToken: keystoneInterfaceAccessToken }));
    }, [keystoneInterfaceAccessToken, dispatch]);

    useEffect(() => {
        if (selectedObject) {
            dispatch(openTrayTypeWithData({
                trayOpen: true,
                trayData: selectedObject,
                trayType: "shipment"
            }));
        }
    }, [dispatch, selectedObject]);

    const handleOnClick = useCallback((shipment) => {
        navigate(`/${page}/${tabName}?id=${shipment.id}`);
    }, [navigate, page, tabName]);

    const ShipmentIcon = ({ shipment }) => {
        const { hue, sat, light } = colorFromNumber(shipment.offer?.offer_number);
        const tint = `hsl(${hue} ${sat}% ${light}%)`;
        const src =
            tabName === 'shipped'   ? ShipmentShipped   :
            tabName === 'delivered' ? ShipmentDelivered : AwaitingShipment;

        return (
            <div style={{position:'relative', width:64}}>
                <img
                    src={src}
                    style={{width:'100%', display:'block'}}
                    alt=""
                />

                <div
                    style={{
                    position:'absolute',
                    inset:0,
                    backgroundColor:tint,
                    mixBlendMode:'color',
                    borderRadius:6,
                    pointerEvents:'none',
                    opacity: 0.8
                    }}
                />
            </div>
        );
    };

    const shipments = useMemo(() => {
        return keystoneInventoryShipments?.filter((shipment) =>
            (keystoneInterfaceShipmentsTab === 0 && shipment.status === "PENDING") ||
            (keystoneInterfaceShipmentsTab === 1 && (shipment.status === "SHIPPED" || shipment.status === "COMPLETED"))).sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
    }, [keystoneInventoryShipments, keystoneInterfaceShipmentsTab]);
        
    return (
        <div className="keystone-shipments list-container product-vertical-container flex-1 overflow-auto">
            <div className="product-vertical-container flex-1 v-gap-l overflow-auto">
                <div className="pad-l no-pad-bot">
                    <Text header={2} size="h2" bold={true}>{tabName === "awaiting-shipment" ? "Awaiting Shipment" : "Shipped"}</Text>
                </div>
                {shipments?.length > 0 && (
                    <div className="product-vertical-container flex-1 v-gap-s pad-m no-pad-top overflow-auto">
                        {shipments.map((shipment, index) => (
                            <div className={`flex horizontal keystone-card keystone-row pad-m ${selectedObjectId === shipment.id ? "keystone-row-active" : ""} space-between clickable`} key={index} onClick={(e) => handleOnClick(shipment)}>
                                <div className="flex horizontal gap-l">
                                    <ShipmentIcon shipment={shipment} />
                                    <div className="flex vertical">
                                        <Text  size="paragraph" mono={true} bold={true}>{shipment.shipment_number.toString().padStart(12,'0').replace(/(\d{4})(\d{4})(\d{4})/, '$1-$2-$3')}</Text>
                                        <Text size="caption" variant="secondary" bold={true}>Warehouse: {shipment.warehouse_name}</Text>
                                    </div>
                                </div>
                                <div className="flex vertical align-start">
                                    <Text size="paragraph" mono={true} bold={true}>
                                        {new Date(new Date(shipment.created_at).getTime() + 3 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit" })}
                                    </Text>
                                    <Text size="caption" variant="secondary" bold={true}>Ship-by Date</Text>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {keystoneInterfaceShipmentsTab === 0 && !keystoneInventoryShipments?.some(shipment => (
                    shipment.status === "PENDING")
                ) && (
                    <Placeholder label="You're all caught up!" />
                )}
                {keystoneInterfaceShipmentsTab === 1 && !keystoneInventoryShipments?.some(shipment => (
                    shipment.status === "SHIPPED")
                ) && (
                    <Placeholder label="You're all caught up!" />
                )}
                {keystoneInterfaceShipmentsTab === 2 && !keystoneInventoryShipments?.some(shipment => (
                    shipment.status === "COMPLETED"
                )) && (
                    <Placeholder label="You're all caught up!" />
                )}
            </div>
        </div>
    );
};

export default React.memo(Shipments);